import { useState, useCallback, useMemo, useEffect } from 'react';
import { mergeEdges } from '@/lib/pagination';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import { Plus, Upload, Rocket, LayoutTemplate, Rss } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@scio3/ui';
import {
  ASSETS_QUERY,
  ASSET_GROUPS_QUERY,
  SEARCH_ASSETS,
  SEARCH_GROUPS,
  SAVE_ASSET,
} from '@scio3/graphql-client';
import type { Asset, AssetGroup, SortDirection, QueryAssetInput } from '@scio3/shared-types';
import {
  ASSET_TYPES,
  FILE_ASSET_STATUS,
  GROUP_TYPES,
  SORT_DIRECTION,
  buildAssetPickerFilter,
  applyFileTypeFilter,
  isAssetExcludedFromPicker,
  ASSET_PICKER_TYPES,
} from '@scio3/shared-types';
import { useTeamStore } from '@/stores/team-store';
import { useAppSelectorStore } from '@/stores/app-selector-store';
import { useAssetDialogStore } from '@/features/assets/stores/asset-dialog-store';
import { usePermissions } from '@/hooks/usePermissions';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';
import { useSharedFolders } from '@/hooks/useSharedFolders';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useCurrentGroup } from '@/features/assets/hooks/useCurrentGroup';
import {
  useTransloaditUpload,
  type UploadFileMeta,
} from '@/features/assets/hooks/useTransloaditUpload';
import { useAssetSubscriptions } from '@/features/assets/hooks/useAssetSubscriptions';
import { useConvertingAssetPoller } from '@/features/assets/hooks/useConvertingAssetPoller';
import { usePlaylistSelectionStore } from '../../stores/playlist-selection-store';
import { useFolderAdd } from '../../hooks/useFolderAdd';
import { usePlaylistDnd } from '../PlaylistDndProvider';
import {
  AssetPickerSearch,
  PICKER_TYPE_ALL,
  PICKER_TYPE_SHARED,
  PICKER_FILE_TYPE_FILTERS,
} from './AssetPickerSearch';
import { AssetPickerList } from './AssetPickerList';
import { FolderBreadcrumb } from '@/features/screens/components/FolderBreadcrumb';

const PAGE_SIZE = 50;

interface AssetsQueryResult {
  assets: {
    page: {
      edges: Array<{ cursor?: string; node: Asset }>;
      pageInfo: { hasNextPage: boolean; endCursor?: string };
    };
    pageData?: { total?: number };
  };
}

interface GroupsQueryResult {
  groups: {
    page: {
      edges: Array<{ cursor?: string; node: AssetGroup }>;
      pageInfo?: { hasNextPage: boolean; endCursor?: string };
    };
    pageData?: { total?: number };
  };
}

interface SearchAssetsResult {
  searchAssets: Asset[];
}

interface SearchGroupsResult {
  searchGroups: AssetGroup[];
}

interface AssetPickerPanelProps {
  /** Whether the user can edit the playlist (folder permission check). */
  canEditPlaylist?: boolean;
}

/** Remembers the last browsed folder path across remounts (SPA navigation). */
let lastPickerPath = '/';

/** Resets the picker path to root. Called on logout to prevent cross-account path leakage. */
export function resetPickerPath() {
  lastPickerPath = '/';
}

export function AssetPickerPanel({ canEditPlaylist = true }: AssetPickerPanelProps) {
  const { t } = useTranslation();
  const { activeTeamId } = useTeamStore();
  const { activePlaylistId } = usePlaylistSelectionStore();
  const { folderLevelSecurity } = useCompanyFeatures();
  const { cancelAutoSave, addAssetsToEditor, addFolderAssetsToEditor } = usePlaylistDnd();
  const { resolveFolderAndConfirm } = useFolderAdd({ cancelAutoSave });

  // Level 1: Role-only permissions (for SHOW/HIDE decisions)
  const rolePermissions = usePermissions();
  const showCreateAsset = rolePermissions.canCreate('asset');

  // Local picker state (declared before callbacks that reference them)
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>(PICKER_TYPE_ALL);
  const [currentPath, setCurrentPath] = useState(lastPickerPath);
  // Track folder's owning teamId for shared folders (cross-team access)
  const [folderTeamId, setFolderTeamId] = useState<string | undefined>(undefined);
  // Resolve effective teamId: folder's owning teamId for shared folders, else activeTeamId
  const effectiveTeamId = useEffectiveTeamId(
    currentPath === '/' ? null : currentPath,
    folderTeamId,
  );

  // Sync module-level variable so path survives remounts
  useEffect(() => {
    lastPickerPath = currentPath;
  }, [currentPath]);

  // Level 2: Role + Folder permissions (for ENABLE/DISABLE decisions)
  // Fetch current folder group for security checks
  const { currentGroup } = useCurrentGroup(currentPath, folderTeamId);
  const folderContext = currentGroup ? { parentFolder: currentGroup } : {};
  const { canCreate, hasFolderAccess } = usePermissions(folderContext);
  const canCreateAsset = canCreate('asset');
  // hasFolderAccess = false when folder has isFolderSecurityEdit=false AND user is not privileged
  const isReadOnlyFolder = folderLevelSecurity && !hasFolderAccess;

  // --- Upload: Transloadit upload (same pattern as AssetsPage) ---
  const [saveAssetMutation] = useMutation(SAVE_ASSET, {
    refetchQueries: ['Assets', 'AssetGroups'],
  });

  const handleUploadSuccess = useCallback(
    (fileMeta: UploadFileMeta, assemblyId: string, bucket?: string) => {
      saveAssetMutation({
        variables: {
          teamId: activeTeamId,
          payload: {
            teamId: activeTeamId,
            type: ASSET_TYPES.FILE,
            status: FILE_ASSET_STATUS.CONVERTING,
            processId: assemblyId,
            originalFileExtension: fileMeta.type,
            originalFileSize: fileMeta.size,
            fileSize: fileMeta.size,
            originalFileName: fileMeta.name,
            path: currentPath === '/' ? null : currentPath,
            groupId: null,
            ...(bucket ? { bucket } : {}),
          },
        },
      }).catch(() => {
        toast.error(
          t('Failed to save asset "{{name}}". Please try again.', { name: fileMeta.name }),
        );
      });
    },
    [saveAssetMutation, activeTeamId, t, currentPath],
  );

  const handleAllUploadsComplete = useCallback(() => {
    toast.success(t('Your files successfully uploaded and being processed.'));
  }, [t]);

  const { open: openUploadModal } = useTransloaditUpload({
    onUploadSuccess: handleUploadSuccess,
    onAllComplete: handleAllUploadsComplete,
  });
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>(SORT_DIRECTION.DESC);

  const isRoot = currentPath === '/';
  // Atlas Search autocomplete requires min 2 chars; 1-char queries use client-side filter
  const isSearching = searchText.length >= 2;
  const isSharedMode = typeFilter === PICKER_TYPE_SHARED;

  // Search fileType: map typeFilter to fileType for Atlas Search
  const searchFileType =
    typeFilter !== PICKER_TYPE_ALL &&
    (PICKER_FILE_TYPE_FILTERS as readonly string[]).includes(typeFilter)
      ? typeFilter
      : undefined;

  // Atlas Search queries — used when searchText is present (fuzzy, relevance-ranked)
  const searchPath = isRoot ? undefined : currentPath;
  const {
    data: searchData,
    loading: searchLoading,
    fetchMore: fetchMoreSearch,
  } = useQuery<SearchAssetsResult>(SEARCH_ASSETS, {
    variables: {
      teamId: activeTeamId,
      input: {
        query: searchText || '',
        limit: PAGE_SIZE,
        path: searchPath,
        fileType: searchFileType,
      },
    },
    skip: !activeTeamId || !isSearching || isSharedMode,
    fetchPolicy: 'network-only',
  });

  const { data: searchGroupsData, loading: searchGroupsLoading } = useQuery<SearchGroupsResult>(
    SEARCH_GROUPS,
    {
      variables: {
        teamId: activeTeamId,
        input: { query: searchText || '', limit: PAGE_SIZE, type: GROUP_TYPES.ASSET },
      },
      skip: !activeTeamId || !isSearching || isSharedMode,
      fetchPolicy: 'network-only',
    },
  );

  // Build query input for assets (regular browse mode)
  // Start with playlist-specific exclusions (audio, QR, emergency apps, etc.)
  const playlistFilter = buildAssetPickerFilter(ASSET_PICKER_TYPES.PLAYLIST);
  const queryInput: Record<string, unknown> = { ...playlistFilter };
  if (typeFilter !== PICKER_TYPE_ALL && !isSharedMode) {
    if ((PICKER_FILE_TYPE_FILTERS as readonly string[]).includes(typeFilter)) {
      applyFileTypeFilter(queryInput as Partial<QueryAssetInput>, typeFilter);
    }
  }
  if (!isRoot) {
    queryInput.path = currentPath;
    // query.teamId coupled with path — use effectiveTeamId (folder's owning team) for shared folders
    if (effectiveTeamId) queryInput.teamId = effectiveTeamId;
  } else {
    queryInput.path = null;
  }

  // Build asset sort — AssetSort uses 'originalFileName' instead of 'name'
  const assetSortField = sortField === 'name' ? 'originalFileName' : sortField;
  const assetSort = { [assetSortField]: sortDirection };

  // Regular assets query — skip when searching (Atlas Search handles it)
  const { data, loading, fetchMore, refetch } = useQuery<AssetsQueryResult>(ASSETS_QUERY, {
    variables: {
      first: PAGE_SIZE,
      teamId: activeTeamId,
      query: queryInput,
      sort: assetSort,
      extendParams: {},
    },
    skip: !activeTeamId || isSearching || isSharedMode,
    fetchPolicy: 'cache-and-network',
  });

  // Separate groups query for folders — skip when searching (SEARCH_GROUPS handles it).
  // teamId promoted to top-level arg (removed from query input).
  const groupsQueryInput: Record<string, unknown> = {
    type: GROUP_TYPES.ASSET.toUpperCase(),
    // query.teamId coupled with path — use effectiveTeamId (folder's owning team) for shared folders
    ...(isRoot ? {} : { teamId: effectiveTeamId }),
    path: isRoot ? null : currentPath,
  };
  const groupSort = { [sortField]: sortDirection };

  const { data: groupsData, fetchMore: fetchMoreGroups } = useQuery<GroupsQueryResult>(
    ASSET_GROUPS_QUERY,
    {
      variables: {
        first: PAGE_SIZE,
        teamId: activeTeamId,
        query: groupsQueryInput,
        sort: groupSort,
      },
      skip: !activeTeamId || isSearching || isSharedMode,
      fetchPolicy: 'cache-and-network',
    },
  );

  // Shared folders query — only active in "Shared with me" mode
  const { sharedFolders, loading: sharedLoading } = useSharedFolders(GROUP_TYPES.ASSET, {
    skip: !isSharedMode,
  });

  // Derive display data from search or regular queries
  const pageInfo = data?.assets?.page?.pageInfo;

  // Client-side filter for 1-char queries (Atlas Search needs 2+ chars)
  const shortQuery = searchText.length === 1 ? searchText.toLowerCase() : '';

  const assets: Asset[] = useMemo(() => {
    const results = searchData?.searchAssets ?? [];
    const edges = data?.assets?.page?.edges ?? [];
    let raw = isSharedMode ? [] : isSearching ? results : edges.map((e) => e.node);
    // Filter out asset types excluded from playlists (search results bypass server-side filters)
    raw = raw.filter((a) => !isAssetExcludedFromPicker(a, ASSET_PICKER_TYPES.PLAYLIST));
    if (shortQuery) {
      raw = raw.filter(
        (a) =>
          a.originalFileName?.toLowerCase().includes(shortQuery) ||
          a.appType?.toLowerCase().includes(shortQuery) ||
          a.webType?.toLowerCase().includes(shortQuery),
      );
    }
    return raw;
  }, [isSharedMode, isSearching, searchData?.searchAssets, data?.assets?.page?.edges, shortQuery]);

  const folders: AssetGroup[] = useMemo(() => {
    const searchedGroups = searchGroupsData?.searchGroups ?? [];
    const raw = isSharedMode
      ? sharedFolders
      : isSearching
        ? searchedGroups
        : (groupsData?.groups?.page?.edges?.map((e) => e.node) ?? []);
    if (shortQuery) {
      return raw.filter((f) => f.name?.toLowerCase().includes(shortQuery));
    }
    return raw;
  }, [
    isSharedMode,
    isSearching,
    sharedFolders,
    searchGroupsData?.searchGroups,
    groupsData?.groups?.page?.edges,
    shortQuery,
  ]);

  // Real-time asset updates via WebSocket (so uploading assets update from CONVERTING → ACTIVE)
  useAssetSubscriptions(refetch);
  // Poll CONVERTING assets until thumbnail is available
  useConvertingAssetPoller(assets);

  const isLoading = isSharedMode
    ? sharedLoading
    : isSearching
      ? searchLoading || searchGroupsLoading
      : loading;

  const assetTotalCount = data?.assets?.pageData?.total;
  const folderTotalCount = groupsData?.groups?.pageData?.total;

  const searchResultCount = searchData?.searchAssets?.length ?? 0;
  const searchHasMore = searchResultCount > 0 && searchResultCount % PAGE_SIZE === 0;
  const hasNextPage = isSharedMode
    ? false
    : isSearching
      ? searchHasMore
      : (pageInfo?.hasNextPage ?? false);

  // Folder pagination
  const folderPageInfo = groupsData?.groups?.page?.pageInfo;
  const hasFolderNextPage = folderPageInfo?.hasNextPage ?? false;
  const [isFolderLoadingMore, setIsFolderLoadingMore] = useState(false);
  const loadMoreFolders = useCallback(() => {
    const cursor = folderPageInfo?.endCursor;
    if (!hasFolderNextPage || !cursor) return;
    setIsFolderLoadingMore(true);
    fetchMoreGroups({
      variables: { after: cursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          groups: {
            ...fetchMoreResult.groups,
            page: {
              ...fetchMoreResult.groups.page,
              edges: mergeEdges(prev.groups.page.edges, fetchMoreResult.groups.page.edges),
            },
          },
        };
      },
    }).finally(() => {
      setIsFolderLoadingMore(false);
    });
  }, [fetchMoreGroups, folderPageInfo?.endCursor, hasFolderNextPage]);

  const handleLoadMore = useCallback(() => {
    if (isSearching) {
      if (!searchHasMore) return;
      fetchMoreSearch({
        variables: {
          input: {
            query: searchText || '',
            limit: PAGE_SIZE,
            offset: searchResultCount,
            path: searchPath,
            fileType: searchFileType,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            searchAssets: [...(prev.searchAssets ?? []), ...fetchMoreResult.searchAssets],
          };
        },
      });
    } else {
      if (!pageInfo?.hasNextPage || !pageInfo.endCursor) return;
      fetchMore({
        variables: { after: pageInfo.endCursor },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            assets: {
              ...fetchMoreResult.assets,
              page: {
                ...fetchMoreResult.assets.page,
                edges: mergeEdges(prev.assets.page.edges, fetchMoreResult.assets.page.edges),
              },
            },
          };
        },
      });
    }
  }, [
    isSearching,
    searchHasMore,
    fetchMoreSearch,
    searchText,
    searchResultCount,
    searchPath,
    searchFileType,
    pageInfo,
    fetchMore,
  ]);

  const toggleAsset = (id: string) => {
    setSelectedAssetIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleAddSingle = useCallback(
    (asset: Asset) => {
      if (!canEditPlaylist) return;
      if (!activePlaylistId) return;
      if (asset._id === activePlaylistId) return;
      if (isAssetExcludedFromPicker(asset, ASSET_PICKER_TYPES.PLAYLIST)) return;
      addAssetsToEditor([asset._id]);
    },
    [canEditPlaylist, activePlaylistId, addAssetsToEditor],
  );

  const handleSelectAll = useCallback(() => {
    const allIds = assets
      .filter((a) => !isAssetExcludedFromPicker(a, ASSET_PICKER_TYPES.PLAYLIST))
      .map((a) => a._id)
      .filter((id) => id !== activePlaylistId);
    setSelectedAssetIds(new Set(allIds));
  }, [assets, activePlaylistId]);

  const handleAddSelected = useCallback(() => {
    if (!canEditPlaylist) return;
    if (!activePlaylistId || selectedAssetIds.size === 0) return;
    const ids = Array.from(selectedAssetIds).filter((id) => id !== activePlaylistId);
    if (ids.length === 0) return;
    addAssetsToEditor(ids);
    setSelectedAssetIds(new Set());
  }, [canEditPlaylist, activePlaylistId, selectedAssetIds, addAssetsToEditor]);

  const handleNavigateFolder = useCallback((path: string, teamId?: string) => {
    setCurrentPath(path);
    setFolderTeamId(teamId);
    setSelectedAssetIds(new Set());
  }, []);

  const handleAddFolder = useCallback(
    async (folderPath: string, folderName: string) => {
      if (!canEditPlaylist) return;
      if (!activePlaylistId) return;
      resolveFolderAndConfirm(folderPath, folderName, (edges) => {
        addFolderAssetsToEditor(edges);
      });
    },
    [canEditPlaylist, activePlaylistId, resolveFolderAndConfirm, addFolderAssetsToEditor],
  );

  return (
    <div className="flex h-full flex-col pt-3">
      {/* Search + filters + New */}
      <div className="space-y-2 py-2 pr-6">
        <AssetPickerSearch
          searchText={searchText}
          onSearchChange={setSearchText}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={(field, direction) => {
            setSortField(field);
            setSortDirection(direction);
          }}
          folderLevelSecurity={folderLevelSecurity}
        />

        {/* GAP-13: "New" dropdown for quick asset creation (Upload, Apps, Templates, Feeds) */}
        {/* SHOW based on role, DISABLE based on folder. Hide in shared mode. */}
        {showCreateAsset &&
          typeFilter !== 'engage' &&
          !isSharedMode &&
          (canCreateAsset && !isReadOnlyFolder ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="min-w-0 flex-1 w-full">
                  <Plus className="h-4 w-4" />
                  {t('New')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem onClick={() => openUploadModal()}>
                  <Upload className="mr-2 h-4 w-4" />
                  {t('Upload Files')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => useAppSelectorStore.getState().openAppSelector({ currentPath })}
                >
                  <Rocket className="mr-2 h-4 w-4" />
                  {t('Apps')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    window.location.href = '/app/templateManagement';
                  }}
                >
                  <LayoutTemplate className="mr-2 h-4 w-4" />
                  {t('Templates')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    useAssetDialogStore.getState().openDialog('feeds', { currentPath })
                  }
                >
                  <Rss className="mr-2 h-4 w-4" />
                  {t('Feeds')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="outline" className="min-w-0 flex-1 w-full" disabled>
              <Plus className="h-4 w-4" />
              {t('New')}
            </Button>
          ))}
      </div>

      {/* Breadcrumb */}
      {!isRoot && (
        <div className="py-1.5 pr-5">
          <FolderBreadcrumb currentPath={currentPath} onNavigate={handleNavigateFolder} />
        </div>
      )}

      {/* Asset list */}
      <AssetPickerList
        assets={assets}
        folders={folders}
        loading={isLoading}
        totalCount={isSearching ? undefined : assetTotalCount}
        folderTotalCount={isSearching ? undefined : folderTotalCount}
        hasNextPage={hasNextPage}
        hasFolderNextPage={hasFolderNextPage}
        isFolderLoadingMore={isFolderLoadingMore}
        selectedAssetIds={selectedAssetIds}
        activePlaylistId={activePlaylistId}
        currentPath={currentPath}
        onToggleAsset={toggleAsset}
        onAddSingle={handleAddSingle}
        onOpenAsset={(asset) => {
          // Feed assets → feed edit dialog (matches Assets page behavior)
          if (asset.appType === 'feed') {
            useAssetDialogStore.getState().openDialog('feedEdit', { asset });
          } else {
            useAssetDialogStore.getState().openDialog('preview', { asset });
          }
        }}
        onAddSelected={handleAddSelected}
        onSelectAll={handleSelectAll}
        onClearSelection={() => setSelectedAssetIds(new Set())}
        onLoadMore={handleLoadMore}
        onLoadMoreFolders={loadMoreFolders}
        onNavigateFolder={handleNavigateFolder}
        onAddFolder={handleAddFolder}
        canEditPlaylist={canEditPlaylist}
      />
    </div>
  );
}
